import React, { forwardRef } from 'react';
import clsx from 'clsx';
import { BLOCKS } from '@contentful/rich-text-types';
import { Heading } from '~components/Heading';
import { rawContentToReactComponents } from '~utils/rawContentToReactComponents';
import * as styles from './RichContent.module.css';

import type { RenderMark, RenderNode } from '@contentful/rich-text-react-renderer';

export type RichContentType = { raw: string };

export type RichContentProps = {
  className?: string;
  /** Rich content from contentful JSON object */
  content?: RichContentType;
  /** Contentful RenderNode override */
  renderNode?: RenderNode;
  /** Contentful Rendermark override */
  renderMark?: RenderMark;
};

type BlockType = 'Heading2' | 'Paragraph' | 'Table' | 'TableRow' | 'TableHeaderCell' | 'TableCell';

type BlockRegistry = {
  [Key in BlockType]: (props: { children: React.ReactNode; className?: string }) => JSX.Element;
};

export const Blocks: BlockRegistry = {
  Heading2: ({ children, className }) => (
    <Heading level={2} force className={clsx(styles.title, className)}>
      {children}
    </Heading>
  ),
  Paragraph: ({ children, className }) => <div className={clsx(styles.copy, className)}>{children}</div>,
  Table: ({ children, className }) => (
    <div className={clsx(styles.tableWrapper, className)}>
      <table className={styles.table}>
        <tbody>{children}</tbody>
      </table>
    </div>
  ),
  TableRow: ({ children, className }) => <tr className={className}>{children}</tr>,
  TableHeaderCell: ({ children, className }) => <th className={clsx(styles.tableHeaderCell, className)}>{children}</th>,
  TableCell: ({ children, className }) => <td className={clsx(styles.tableCell, className)}>{children}</td>,
};

/* TODO https://insify.atlassian.net/browse/IDEV-4105 */
export const RichContent = forwardRef(
  (
    { content, renderNode, renderMark, className }: RichContentProps,
    ref: React.ForwardedRef<HTMLDivElement>,
  ): JSX.Element => {
    const defaultRenderNode: RenderNode = {
      [BLOCKS.HEADING_2]: (_, children) => <Blocks.Heading2>{children}</Blocks.Heading2>,
      [BLOCKS.PARAGRAPH]: (_, children) => <Blocks.Paragraph>{children}</Blocks.Paragraph>,
      [BLOCKS.TABLE]: (_, children) => <Blocks.Table>{children}</Blocks.Table>,
      [BLOCKS.TABLE_ROW]: (_, children) => <Blocks.TableRow>{children}</Blocks.TableRow>,
      [BLOCKS.TABLE_HEADER_CELL]: (_, children) => <Blocks.TableHeaderCell>{children}</Blocks.TableHeaderCell>,
      [BLOCKS.TABLE_CELL]: (_, children) => <Blocks.TableCell>{children}</Blocks.TableCell>,
    };

    const contentBlock = (): React.ReactNode =>
      content ? (
        rawContentToReactComponents(content.raw, {
          renderNode: { ...defaultRenderNode, ...renderNode },
          renderMark,
        })
      ) : (
        <></>
      );

    return (
      <div className={className} ref={ref}>
        {contentBlock()}
      </div>
    );
  },
);
